/**
 * Recherche prédictive — élément hôte `<predictive-search>`.
 *
 * Responsabilité unique : le cycle de vie et l'orchestration. Il résout les noeuds de
 * son instance, monte ses cinq collaborateurs, les défait — requête en vol, debounces
 * et écouteurs compris — et arbitre la bascule entre l'état sans saisie et l'état
 * résultats. Il ne fait ni réseau, ni ARIA, ni stockage : chacun vit dans son module.
 *
 * ── Pourquoi 6 fichiers d'assets et PAS un bloc `javascript` de section ──────────────
 * La règle du repo veut qu'un composant neuf embarque son JS dans un bloc `javascript`
 * de son fichier `.liquid`. Elle ne s'applique pas ici, et un relecteur qui la ferait
 * appliquer casserait le panneau : le bundle `compiled_assets/*` est généré PAR PAGE à
 * partir du render tree de la page hôte, or la section du panneau n'est rendue que dans
 * la réponse de `/search/suggest`, qui n'est pas une page. Son JS ne serait donc jamais
 * chargé par la page qui en a besoin. Les 6 fichiers sont posés en `<script defer>` dans
 * `layout/theme.liquid`, et l'ordre du document garantit que les 5 modules s'enregistrent
 * avant que cet élément ne les lise — c'est le contrat de la famille `buyback-portal-*`.
 *
 * ── Pourquoi un custom element ET des écouteurs de section explicites ────────────────
 * `connectedCallback` ne suffit pas dans ce thème : le patron maison (`grid-columns-image`,
 * `fmd-collection`) double toujours le cycle de vie natif d'un garde `isInitialized`, d'un
 * rattrapage `DOMContentLoaded` et des deux écouteurs `shopify:section:load` /
 * `shopify:section:unload`. Le header est rechargé par l'éditeur de thème, et sans ces
 * écouteurs une instance survit détachée avec sa requête en vol.
 * À la différence du patron, on interroge ici `querySelectorAll` et non `querySelector` :
 * `sections/header.liquid` et `sections/header-fmd.liquid` portent DEUX formulaires
 * chacun, donc deux instances dans la même section.
 *
 * Complété par : T21 (soumission unique), T27 (exclusion mutuelle des panneaux — le
 * recouvrement 769-989 px anticipé par le plan ne se produit pas dans l'état actuel du
 * thème, cf. le commentaire de `registry.instances` ; l'exclusion protège surtout le
 * drawer et `#MobileSearchContainer`, deux déclencheurs frères toujours visibles ensemble).
 * La bascule des deux états, le chargement, l'état vide et l'erreur vivent dans
 * `predictive-search-states.js` (T22) ; le jeu d'events dataLayer vit dans
 * `predictive-search-tracking.js` (T37) — les deux montés comme les autres.
 */
(function (window, document) {
  'use strict';

  var registry = (window.MintPredictiveSearch = window.MintPredictiveSearch || {});

  /**
   * Registre des instances vivantes. T27 ferme les autres panneaux à l'ouverture d'un
   * panneau, SANS `document.querySelector` global : il lit ce Set (peuplé par `init()`,
   * vidé par `destroy()`).
   *
   * MESURÉ le 2026-09-16 (127.0.0.1:9292, sweep 767→1024px, anti-pattern n°17 — ne jamais
   * supposer qu'une classe `--hide` et une media query voisine partagent le même
   * breakpoint) : la prémisse du plan (« le champ desktop et #MobileSearchContainer sont
   * visibles ensemble entre 769 et 989 px ») ne se vérifie PAS dans l'état actuel du
   * thème. `medium-down--hide` est défini DEUX FOIS avec des seuils différents —
   * `assets/mint.css:6558` le limite à `max-width:768px`, mais `assets/theme.css.liquid:778`
   * (jamais édité, legacy) le définit aussi à `max-width:989px`. Les deux règles sont
   * `!important` et n'entrent JAMAIS en concurrence directe (leurs media queries ne se
   * recouvrent pas) : celle de mint.css est simplement inactive au-delà de 768px, et celle
   * de theme.css.liquid reste seule active jusqu'à 989px. Résultat mesuré : le conteneur
   * desktop (`.header-layout.medium-down--hide`) reste `display:none` jusqu'à 989px inclus
   * et ne s'affiche qu'à PARTIR de 990px — exactement la frontière de `tablet-up--hide`.
   * Les deux champs de la même section (desktop / `#MobileSearchContainer`) sont donc
   * aujourd'hui parfaitement complémentaires, sans recouvrement. Voir anti-pattern n°41.
   * L'exclusion mutuelle reste néanmoins nécessaire et n'est PAS retirée, pour deux raisons
   * VÉRIFIÉES (pas supposées) :
   *   1. Le drawer (`#NavDrawer`) et l'accordéon `#MobileSearchContainer` s'excluent déjà
   *      VISUELLEMENT — `assets/theme.js` (`openNavDrawer`/`openSearchDrawer`) ferme l'un
   *      quand l'autre s'ouvre par son propre déclencheur. Mais cette fermeture ne touche
   *      QUE le conteneur qu'elle referme ; elle ne dit rien à l'instance `<predictive-search>`
   *      qui vit dans L'AUTRE. Mesuré : ouvrir le panneau du drawer, puis cliquer le
   *      déclencheur mobile (qui referme le drawer côté thème) sans jamais fermer le
   *      panneau du drawer soi-même — sans le registre, ce panneau reste
   *      `aria-expanded="true"` alors que son conteneur a disparu. `handleOpen()` couvre ce
   *      cas précisément parce que l'instance mobile n'a pas besoin de connaître le drawer :
   *      elle émet juste `open`, et le registre ferme tout le reste.
   *   2. Le jour où la duplication de `.medium-down--hide` serait corrigée (elle n'a aucune
   *      raison de le rester), le recouvrement 769-989 px prévu par le plan réapparaîtrait
   *      immédiatement — sans qu'aucun autre fichier n'ait besoin de changer.
   */
  registry.instances = registry.instances || new Set();

  /**
   * Garde du kill switch. `settings.predictive_search_enabled` est exposé par
   * `layout/theme.liquid` sous `theme.settings.predictiveSearch` : réglage à `false`,
   * aucune instance ne se monte et aucun écouteur n'est posé.
   */
  function isEnabled() {
    return !!(window.theme && window.theme.settings && window.theme.settings.predictiveSearch);
  }

  /**
   * Section demandée quand le parcours FMD (partenaire ou générique) est actif.
   * Jumelle de `sections/predictive-search-fmd.liquid`.
   */
  var FMD_SECTION_ID = 'predictive-search-fmd';

  /**
   * Aiguillage FMD du panneau (T40, option C′) : l'ÉTAT CLIENT fait foi, jamais l'URL. Le
   * canal URL est contaminé par le cache de page Shopify — un `fmd=` ou un
   * `view=parcours-fmd` lus sur `window.location` peuvent appartenir à la réponse mise en
   * cache d'un AUTRE visiteur (M7/M8, anti-pattern n°38, `state/fmd-cache-diagnostic.md`).
   * `document.body.classList.contains('is-fmd')` (`layout/theme.liquid`) commettrait la
   * même faute : vérifié, cette classe dérive du même `fmd` lu dans l'URL par
   * `snippets/fmd-handle.liquid` — ce n'est pas un marqueur fiable, il n'est donc jamais lu
   * ici, malgré son apparence de raccourci.
   * Trois signaux client, un seul suffit : `MintFmdData.getStatus().isValid` (flux
   * générique, données de simulation encore valides), `localStorage['fmd']` (handle
   * partenaire) et `localStorage['fmd-generic-flow']` (flux générique actif, cas où le
   * flag survit à une donnée déjà relue). `MintFmdData` est posé en script inline
   * synchrone du `<head>` (`layout/theme.liquid`, repère `:207`) : toujours disponible
   * avant ce fichier, chargé en `defer`.
   * Rend `null` en parcours standard : l'appelant laisse alors `createFetcher` appliquer
   * son propre repli, ce fichier n'a pas à connaître ce littéral.
   */
  function resolveFmdSectionId() {
    try {
      if (window.MintFmdData && typeof window.MintFmdData.getStatus === 'function') {
        var status = window.MintFmdData.getStatus();
        if (status && status.isValid) return FMD_SECTION_ID;
      }
      if (window.localStorage.getItem('fmd')) return FMD_SECTION_ID;
      if (window.localStorage.getItem('fmd-generic-flow') === 'true') return FMD_SECTION_ID;
    } catch (error) {
      /** localStorage indisponible (navigation privée, quota) : repli standard. */
    }
    return null;
  }

  /**
   * Garde de capacité boutique, repris du module legacy (`assets/theme.js`, ancre
   * `var supportedShopifyFeatures = JSON.parse(...)`) : `content_for_header` publie dans
   * `#shopify-features` si la boutique sert `/search/suggest`. Sans lui, chaque frappe
   * partirait vers un endpoint absent et le panneau resterait vide sans rien dire.
   * Noeud manquant ou illisible ne vaut PAS « incapacité » — il est rendu par le layout,
   * donc absent d'un rendu de section isolé : on échoue ouvert, le réseau tranchera.
   * Mémorisé, sinon les 3 instances d'une page reparsent le même noeud.
   */
  var shopCapability = null;

  function isShopCapable() {
    if (shopCapability !== null) return shopCapability;

    var node = document.getElementById('shopify-features');
    if (!node) {
      shopCapability = true;
      return shopCapability;
    }

    try {
      shopCapability = !!JSON.parse(node.innerHTML).predictiveSearch;
    } catch (error) {
      shopCapability = true;
    }
    return shopCapability;
  }

  /**
   * Monte un collaborateur si son module est chargé. Retirer un fichier d'asset doit
   * dégrader le panneau, pas produire une cascade d'erreurs : le module absent rend
   * `null`, et ses consommateurs gardent leur appel.
   */
  function mount(module, options) {
    if (!module || typeof module.create !== 'function') return null;
    return module.create(options);
  }

  function unmount(collaborator) {
    if (collaborator && typeof collaborator.destroy === 'function') collaborator.destroy();
    return null;
  }

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.isInitialized = false;
      this.location = '';
      this.form = null;
      this.input = null;
      this.wrapper = null;
      this.results = null;
      this.empty = null;
      this.recentContainer = null;
      this.recentTemplate = null;
      this.searchButton = null;
      this.fetcher = null;
      this.combobox = null;
      this.recent = null;
      this.states = null;
      /** Sixième collaborateur (T37, jeu d'events dataLayer) : monté en dernier dans `init()`. */
      this.tracking = null;

      /**
       * Bindé une seule fois : `listen()` doit recevoir la MÊME référence à l'ajout et au
       * retrait, sinon `destroy()` ne retire rien (piège documenté de `grid-columns-image`,
       * qui rebinde au retrait). Un `.bind()` par appel de `init()` casserait `refresh()`.
       */
      this.handleSearchButtonClick = this.handleSearchButtonClick.bind(this);
      /** Même raison — T27, cf. `handleOpen()`. */
      this.handleOpen = this.handleOpen.bind(this);

      /** Écouteurs posés par l'hôte lui-même. `destroy()` les retire un à un. */
      this.listeners = [];
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      if (this.isInitialized) return;
      if (!isEnabled()) return;
      if (!isShopCapable()) return;
      if (!this.resolveNodes()) return;

      this.fetcher = mount(registry.fetcher, {
        location: this.location,
        sectionId: resolveFmdSectionId(),
      });
      this.combobox = mount(registry.combobox, {
        input: this.input,
        panel: this.wrapper,
        location: this.location,
      });
      this.recent = mount(registry.recent, {
        container: this.recentContainer,
        template: this.recentTemplate,
      });
      /**
       * Monté en DERNIER : il lit les trois autres sur `this` et pose ses écouteurs.
       * Un seul argument, l'hôte lui-même — ce fichier tient le cycle de vie, pas la
       * plomberie d'un quatrième constructeur (T22).
       */
      this.states = mount(registry.states, { host: this });

      /**
       * Correctif n°3 (T21) : le bouton « voir tous les résultats » est `type="button"`
       * (`sections/header.liquid`, `sections/header-fmd.liquid`) — sans ceci, un clic ne
       * fait RIEN. `requestSubmit()`, jamais `submit()` : lui seul déclenche l'event
       * `submit` du formulaire, dont dépend tout traitement de query-string (T42,
       * `view=parcours-fmd`). Optionnel : absent du markup de `snippets/search-bar.liquid`.
       */
      if (this.searchButton) this.listen(this.searchButton, 'click', this.handleSearchButtonClick);

      /**
       * T27 — exclusion mutuelle. `predictive-search:open` est émis par le combobox
       * (`predictive-search-combobox.js`, `emit('open')`) sur `input`, qui bulle jusqu'à
       * CET hôte (l'input est un descendant direct). Écouté ici, pas dans `states.js` : ce
       * n'est pas une question de CE QUI est affiché dans le panneau (T22), mais de combien
       * de panneaux DU DOCUMENT peuvent être ouverts à la fois — la portée du registre, pas
       * celle d'une instance.
       */
      this.listen(this, 'predictive-search:open', this.handleOpen);

      /**
       * Monté en tout dernier (T37) : il lit `combobox` et `states` sur `this`,
       * comme `states` lit les trois précédents. Sixième module plutôt que du
       * code dans cet hôte — le fichier est déjà à son plafond (T22) — sur le
       * même principe : chaque responsabilité neuve reste révocable seule.
       */
      this.tracking = mount(registry.tracking, { host: this });

      registry.instances.add(this);
      this.isInitialized = true;
    }

    /** Cf. le commentaire de `init()` : jamais `this.form.submit()`. */
    handleSearchButtonClick() {
      if (this.form && typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
    }

    /**
     * T27. Ferme les AUTRES instances vivantes — jamais `document.querySelectorAll`, qui
     * attraperait des panneaux hors de portée ou dupliqués par une section rechargée : le
     * registre est la seule source de vérité, peuplé par `init()` et vidé par `destroy()`.
     * `close(false)` : ne vole jamais le focus de l'instance qui vient de s'ouvrir — celle
     * qui a émis l'event porte déjà le focus de l'utilisateur.
     */
    handleOpen() {
      var self = this;
      registry.instances.forEach(function (instance) {
        if (instance === self) return;
        if (instance.combobox && typeof instance.combobox.close === 'function') {
          instance.combobox.close(false);
        }
      });
    }

    /**
     * Résout les noeuds de CETTE instance, tous scopés à l'élément : une page en porte
     * jusqu'à trois, et un sélecteur de document en attraperait d'autres.
     * Rend `false` si le markup attendu manque — l'instance sort alors sans rien monter.
     */
    resolveNodes() {
      this.form = this.querySelector('[data-predictive-form]');
      this.wrapper = this.querySelector('[data-predictive-wrapper]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.form || !this.wrapper || !this.results) return false;

      this.input = this.form.querySelector('input[name="q"]');
      if (!this.input) return false;

      this.location = this.form.getAttribute('data-predictive-location') || '';
      if (!this.location) return false;

      this.empty = this.querySelector('[data-predictive-empty]');
      this.recentContainer = this.querySelector('[data-predictive-recent]');
      this.recentTemplate = this.querySelector('template[data-predictive-recent-item]');
      this.searchButton = this.querySelector('[data-predictive-search-button]');
      return true;
    }

    /**
     * Pose un écouteur en gardant de quoi le retirer — c'est ce qui rend `destroy()`
     * complet, donc `refresh()` sûr. `grid-columns-image` montre le piège inverse : il
     * retire son `resize` avec un `.bind()` neuf, qui ne correspond à aucun écouteur
     * posé, et fuit à chaque rechargement de section. T21, T22 et T27 passent par ici
     * et ne font jamais `addEventListener` en direct.
     */
    listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.listeners.push({ target: target, type: type, handler: handler, options: options });
    }

    /**
     * Idempotent : `disconnectedCallback` et `shopify:section:unload` l'appellent tous
     * deux au même rechargement. Le `destroy()` du fetcher EST son `abort()` — il annule
     * la requête en vol ET le debounce de 250 ms (`predictive-search-fetch.js`) ; le
     * combobox et les récentes tiennent le registre de leurs propres écouteurs.
     */
    destroy() {
      /** Défait avant ce qu'il lit : mounté en dernier, il se défait en premier. */
      this.tracking = unmount(this.tracking);
      this.states = unmount(this.states);
      this.fetcher = unmount(this.fetcher);
      this.combobox = unmount(this.combobox);
      this.recent = unmount(this.recent);

      this.listeners.forEach(function (entry) {
        entry.target.removeEventListener(entry.type, entry.handler, entry.options);
      });
      this.listeners = [];

      registry.instances.delete(this);
      this.isInitialized = false;
    }

    /**
     * On défait avant de refaire, jamais l'inverse. Synchrone, à la différence de
     * `grid-columns-image` qui attend 100 ms : lui attend Flickity, nous n'avons aucune
     * dépendance externe, et un timer de plus serait un objet de plus à couper.
     */
    refresh() {
      this.destroy();
      this.init();
    }
  }

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }

  function eachInstance(root, callback) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    root.querySelectorAll('predictive-search').forEach(callback);
  }

  /**
   * Cycle de vie au niveau du document.
   *
   * Le rattrapage `DOMContentLoaded` n'est pas décoratif : `connectedCallback` ne voit
   * les enfants que si la définition arrive APRÈS l'analyse du markup — vrai tant que
   * les 6 scripts sont en `defer`, faux le jour où l'un passerait en `async` ou inline.
   * `resolveNodes()` rendrait alors `false` sur un élément vide, et ce rattrapage serait
   * la seule chose à remonter le panneau.
   *
   * Sur `shopify:section:load`, l'élément neuf est déjà monté par son
   * `connectedCallback` : `refresh()` le défait puis le refait — sans effet visible, mais
   * une instance restée non montée (markup incomplet au premier passage) y retrouve un
   * état propre. Le double-binding est écarté deux fois : garde `isInitialized`, et
   * `destroy()` avant chaque `init()`.
   */
  function bindDocumentLifecycle() {
    document.addEventListener('DOMContentLoaded', function () {
      eachInstance(document, function (element) {
        if (!element.isInitialized) element.init();
      });
    });

    document.addEventListener('shopify:section:load', function (event) {
      eachInstance(event.target, function (element) {
        element.refresh();
      });
    });

    document.addEventListener('shopify:section:unload', function (event) {
      eachInstance(event.target, function (element) {
        element.destroy();
      });
    });
  }

  /**
   * Le garde de `init()` suffit à ce qu'aucune instance ne se monte ; il ne suffit pas à
   * ce qu'AUCUN écouteur ne soit posé, ce que la vérification de T18 exige. Ces trois-là
   * ne servent que des instances montées. `customElements.define` reste inconditionnel :
   * un élément défini mais jamais monté est inerte, un élément non défini changerait ce
   * que `:defined` sélectionne dans la feuille du panneau (T28).
   * `lifecycleBound` vit sur le registre : si ce fichier était évalué deux fois,
   * `customElements.get` protégerait la définition, pas ces trois écouteurs — qui
   * feraient alors deux `refresh()` par rechargement de section.
   */
  if (isEnabled() && !registry.lifecycleBound) {
    registry.lifecycleBound = true;
    bindDocumentLifecycle();
  }
})(window, document);
